import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Tag } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { useLang, tField } from "../lib/LanguageContext";
import { api, imageUrl } from "../lib/api";
import SEO, { articleSchema, breadcrumbSchema } from "../components/SEO";

const CAT_LABEL = {
    al: { beaches: "Plazhe", restaurant: "Restorant", attractions: "Atraksione", tips: "Këshilla", general: "Tjera" },
    en: { beaches: "Beaches", restaurant: "Restaurant", attractions: "Attractions", tips: "Tips", general: "Other" },
    it: { beaches: "Spiagge", restaurant: "Ristorante", attractions: "Attrazioni", tips: "Consigli", general: "Altro" },
    de: { beaches: "Strände", restaurant: "Restaurant", attractions: "Sehenswürdigkeiten", tips: "Tipps", general: "Sonstiges" },
};

function formatDate(iso, lang) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const locale = lang === "al" ? "sq-AL" : lang === "it" ? "it-IT" : lang === "de" ? "de-DE" : "en-GB";
        return d.toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" });
    } catch {
        return "";
    }
}

export default function BlogPost() {
    const { slug } = useParams();
    const { lang } = useLang();
    const navigate = useNavigate();
    const [post, setPost] = useState(null);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setPost(null);
        setNotFound(false);
        api.get(`/blog/${slug}`)
            .then((r) => setPost(r.data))
            .catch(() => setNotFound(true));
    }, [slug]);

    if (notFound) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7] pt-32 pb-20 px-6" data-testid="blog-not-found">
                <div className="max-w-md text-center">
                    <h1 className="font-serif text-3xl text-[#0F3A59]">
                        {lang === "al" ? "Postimi nuk u gjet" : "Post not found"}
                    </h1>
                    <p className="text-[#1C1C1C]/65 font-light mt-4">
                        {lang === "al" ? "Mund të jetë fshirë ose URL është e gabuar." : "It may have been deleted or the URL is incorrect."}
                    </p>
                    <button
                        onClick={() => navigate("/blog")}
                        className="mt-6 inline-flex items-center gap-2 bg-[#0F3A59] text-white px-6 py-3 rounded-full text-sm"
                    >
                        <ArrowLeft size={14} />
                        {lang === "al" ? "Kthehu te Blog" : "Back to Blog"}
                    </button>
                </div>
            </div>
        );
    }

    if (!post) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
                <div className="text-[#0F3A59]/60 font-light">Loading...</div>
            </div>
        );
    }

    const title = tField(post, "title", lang);
    const excerpt = tField(post, "excerpt", lang);
    const content = tField(post, "content", lang);
    const cats = CAT_LABEL[lang] || CAT_LABEL.en;

    return (
        <div data-testid="blog-post" className="bg-[#FDFBF7]">
            <SEO
                title={title}
                description={excerpt}
                path={`/blog/${slug}`}
                image={imageUrl(post.cover_image)}
                schemas={[
                    articleSchema(post, lang),
                    breadcrumbSchema([
                        { name: lang === "al" ? "Kreu" : "Home", url: "/" },
                        { name: "Blog", url: "/blog" },
                        { name: title, url: `/blog/${slug}` },
                    ]),
                ]}
            />

            {/* Hero */}
            <section className="relative h-[55vh] min-h-[400px] flex items-end overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${imageUrl(post.cover_image, imageUrl("seed:gallery_exterior"))})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/30" />
                <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 pb-12 pt-32 w-full">
                    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
                        <Link
                            to="/blog"
                            data-testid="blog-back"
                            className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm mb-5 transition-colors"
                        >
                            <ArrowLeft size={14} />
                            {lang === "al" ? "Të gjitha postimet" : lang === "it" ? "Tutti gli articoli" : lang === "de" ? "Alle Beiträge" : "All posts"}
                        </Link>
                        <div className="flex items-center gap-4 text-xs text-white/75 mb-4">
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#CBA871]/90 text-white rounded-full uppercase tracking-wider">
                                <Tag size={11} />
                                {cats[post.category] || cats.general}
                            </span>
                            {post.published_at && (
                                <span className="inline-flex items-center gap-1.5">
                                    <Calendar size={12} />
                                    {formatDate(post.published_at, lang)}
                                </span>
                            )}
                        </div>
                        <h1 className="font-serif text-white text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight">
                            {title}
                        </h1>
                    </motion.div>
                </div>
            </section>

            {/* Content */}
            <section className="py-16 md:py-20 px-6 md:px-12 max-w-3xl mx-auto">
                {excerpt && (
                    <p className="text-lg md:text-xl text-[#1C1C1C]/75 font-light leading-relaxed mb-10 italic border-l-2 border-[#CBA871] pl-5">
                        {excerpt}
                    </p>
                )}
                <article className="blog-content prose-custom" data-testid="blog-content">
                    <ReactMarkdown
                        components={{
                            h1: ({ children }) => <h1 className="font-serif text-3xl md:text-4xl text-[#0F3A59] mt-10 mb-4">{children}</h1>,
                            h2: ({ children }) => <h2 className="font-serif text-2xl md:text-3xl text-[#0F3A59] mt-10 mb-4 tracking-tight">{children}</h2>,
                            h3: ({ children }) => <h3 className="font-serif text-xl md:text-2xl text-[#0F3A59] mt-8 mb-3">{children}</h3>,
                            p: ({ children }) => <p className="text-base md:text-lg text-[#1C1C1C]/80 font-light leading-relaxed my-5">{children}</p>,
                            ul: ({ children }) => <ul className="list-disc pl-6 space-y-2 my-5 text-[#1C1C1C]/80 font-light">{children}</ul>,
                            ol: ({ children }) => <ol className="list-decimal pl-6 space-y-2 my-5 text-[#1C1C1C]/80 font-light">{children}</ol>,
                            li: ({ children }) => <li className="leading-relaxed">{children}</li>,
                            strong: ({ children }) => <strong className="text-[#0F3A59] font-semibold">{children}</strong>,
                            em: ({ children }) => <em className="italic text-[#1C1C1C]/70">{children}</em>,
                            blockquote: ({ children }) => (
                                <blockquote className="border-l-4 border-[#CBA871] bg-[#F4EFE6] px-5 py-3 my-6 italic text-[#1C1C1C]/75">
                                    {children}
                                </blockquote>
                            ),
                            hr: () => <hr className="my-10 border-[#E5E0D8]" />,
                            a: ({ href, children }) => (
                                <a href={href} target="_blank" rel="noreferrer" className="text-[#0F3A59] underline decoration-[#CBA871] decoration-2 underline-offset-4 hover:decoration-[#0F3A59]">
                                    {children}
                                </a>
                            ),
                            img: ({ src, alt }) => (
                                <img
                                    src={src}
                                    alt={alt || ""}
                                    decoding="async"
                                    loading="lazy"
                                    className="rounded-2xl w-full my-8 shadow-[0_8px_30px_rgba(0,0,0,0.08)]"
                                />
                            ),
                            code: ({ children }) => <code className="px-1.5 py-0.5 bg-[#F4EFE6] rounded text-sm text-[#0F3A59]">{children}</code>,
                        }}
                    >
                        {content}
                    </ReactMarkdown>
                </article>

                {/* CTA */}
                <div className="mt-16 bg-[#0F3A59] rounded-2xl p-8 md:p-10 text-center text-white">
                    <h3 className="font-serif text-2xl md:text-3xl">
                        {lang === "al" ? "Gati për pushimet?" : lang === "it" ? "Pronto per la vacanza?" : lang === "de" ? "Bereit für den Urlaub?" : "Ready for your holiday?"}
                    </h3>
                    <p className="text-white/75 font-light mt-3 max-w-xl mx-auto">
                        {lang === "al" ? "Rezervo dhomën tënde në Kompleksin Dea Radhimë dhe përjeto vetë magjinë e Rivierës." : "Book your room at Kompleksi Dea Radhimë and experience the magic of the Riviera yourself."}
                    </p>
                    <Link
                        to="/book"
                        data-testid="blog-cta-book"
                        className="inline-block mt-6 bg-white hover:bg-[#CBA871] hover:text-white text-[#0F3A59] px-8 py-3 rounded-full text-sm font-medium transition"
                    >
                        {lang === "al" ? "Rezervo Tani" : lang === "it" ? "Prenota Ora" : lang === "de" ? "Jetzt Buchen" : "Book Now"}
                    </Link>
                </div>
            </section>
        </div>
    );
}
